import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronDownIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { SectionStatusIndicator } from "./SectionStatusIndicator";
import { Column, Row, Text } from "@/components";
import { ConfigFormWrapper } from "@/components/companies/shared/ConfigFormWrapper";
import { FormButtonGroup } from "@/components/form/FormButtonGroup";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useCreateJobDifficultyUrgency } from "@/hooks/mutations/useCreateJobDifficultyUrgency";
import { useGetJobDifficultyUrgencies } from "@/hooks/queries/useGetJobDifficultyUrgencies";
import { useAddJobDifficultyUrgency } from "@/hooks/sets/useAddJobDifficultyUrgency";
import {
  jobDifficultyOptions,
  jobDifficultyToBgClasses,
  jobDifficultyToDotColor,
  jobDifficultyToLabel,
  jobUrgencyOptions,
  jobUrgencyToBgClasses,
  jobUrgencyToIcon,
  jobUrgencyToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { difficultyUrgencySchema } from "@/lib/schemas";
import { cn } from "@/lib/utils";
import { JobDifficulty, JobUpdate, JobUrgency } from "@/types";

const difficultyDotIcons: Record<string, React.ElementType> =
  Object.fromEntries(
    Object.entries(jobDifficultyToDotColor).map(([value, color]) => [
      value,
      () => <span className={cn("h-2 w-2 rounded-full", color)} />,
    ]),
  );

const difficultySelectedClassName: Record<string, string> = Object.fromEntries(
  Object.entries(jobDifficultyToBgClasses).map(([value, bg]) => [
    value,
    `${bg} border-transparent`,
  ]),
);

const urgencySelectedClassName: Record<string, string> = Object.fromEntries(
  Object.entries(jobUrgencyToBgClasses).map(([value, bg]) => [
    value,
    `${bg} border-transparent`,
  ]),
);

export const DifficultyUrgencySection = () => {
  /* Database CRUD Operations */
  // Create
  const {
    mutate: createJobDifficultyUrgency,
    isPending: isCreatingDifficultyUrgency,
  } = useCreateJobDifficultyUrgency();

  // Read
  const { data: jobDifficultyUrgencies } = useGetJobDifficultyUrgencies();

  /* Misc Hooks */
  const addJobDifficultyUrgency = useAddJobDifficultyUrgency();
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();
  const successToast = useSuccessToast();

  const currentDifficultyUrgency = jobDifficultyUrgencies?.[0];

  const form = useForm<z.infer<typeof difficultyUrgencySchema>>({
    defaultValues: {
      difficulty: JobDifficulty.EASY,
      urgency: JobUrgency.LOW,
    },
    resolver: zodResolver(difficultyUrgencySchema),
  });

  /* React Hooks */
  // useEffect
  useEffect(() => {
    if (currentDifficultyUrgency) {
      form.reset({
        difficulty: currentDifficultyUrgency.difficulty ?? undefined,
        urgency: currentDifficultyUrgency.urgency ?? undefined,
      });
    } else {
      form.reset();
    }
  }, [currentDifficultyUrgency, form]);

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof difficultyUrgencySchema>) => {
    createJobDifficultyUrgency(
      {
        companyId: currentCompanyId,
        createJobDifficultyUrgencyRequest: data,
        jobId: currentJobId,
      },
      {
        onSuccess: (newDifficultyUrgency) => {
          addJobDifficultyUrgency({
            companyId: currentCompanyId,
            jobId: currentJobId,
            newJobDifficultyUrgency: newDifficultyUrgency,
          });
          successToast({
            description: "Difficulty & urgency saved!",
          });
        },
      },
    );
  };

  /* Render */
  const render = () => {
    return (
      <SectionStatusIndicator complete={!!currentDifficultyUrgency}>
        <Collapsible className="group/collapsible">
          <Column className="overflow-hidden rounded-lg border border-slate-200 bg-white">
            <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between px-6 py-4">
              <Text variant="header">Difficulty & Urgency</Text>
              <Row className="items-center gap-2">
                <DifficultyUrgencyStatusBadges
                  currentDifficultyUrgency={currentDifficultyUrgency}
                />
                <ChevronDownIcon className="h-4 w-4 text-slate-500 transition-transform duration-200 group-data-[state=closed]/collapsible:-rotate-90" />
              </Row>
            </CollapsibleTrigger>
            <ConfigFormWrapper
              buttonLabel="Save Difficulty & Urgency"
              disabled={isCreatingDifficultyUrgency}
              form={form}
              lastUpdated={currentDifficultyUrgency?.createdAt}
              onSubmit={handleSubmit}
            >
              <FormButtonGroup
                form={form}
                fieldName="difficulty"
                label="Difficulty"
                options={jobDifficultyOptions}
                valueToSelectedClassName={difficultySelectedClassName}
                valueToIconMap={difficultyDotIcons}
              />
              <FormButtonGroup
                form={form}
                fieldName="urgency"
                label="Urgency"
                options={jobUrgencyOptions}
                valueToSelectedClassName={urgencySelectedClassName}
                valueToIconMap={jobUrgencyToIcon}
              />
            </ConfigFormWrapper>
          </Column>
        </Collapsible>
      </SectionStatusIndicator>
    );
  };

  return render();
};

const DifficultyUrgencyStatusBadges = ({
  currentDifficultyUrgency,
}: {
  currentDifficultyUrgency: JobUpdate | undefined;
}) => {
  /* Render */
  const render = () => {
    if (!currentDifficultyUrgency) {
      return (
        <Badge className="rounded-md border-red-200 bg-red-50 text-red-600">
          Not Set
        </Badge>
      );
    }

    const { createdAt, difficulty, urgency } = currentDifficultyUrgency;

    return (
      <>
        <Badge variant="secondary" className="rounded-md">
          Updated: {dates.formatDateDivider(createdAt)}
        </Badge>
        {difficulty != null && (
          <Badge
            className={cn(
              "rounded-md border-transparent",
              jobDifficultyToBgClasses[difficulty],
            )}
          >
            Difficulty: {jobDifficultyToLabel[difficulty]}
          </Badge>
        )}
        {urgency != null && (
          <Badge
            className={cn(
              "rounded-md border-transparent",
              jobUrgencyToBgClasses[urgency],
            )}
          >
            Urgency: {jobUrgencyToLabel[urgency]}
          </Badge>
        )}
      </>
    );
  };

  return render();
};
